import { ReactElement, useState, useMemo, useCallback, memo } from 'react';
import {
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Alert,
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';

import {
  groupImportErrorsByType,
  getImportErrorTypeOptions,
  downloadImportErrorsCSV,
} from '~utils/order';

import ImportErrorsDetailGroup from './Group';
import { ImportErrorsDetailProps } from './types';
import {
  ImportErrorsDetailContainer,
  ImportErrorsDetailControls,
  ImportErrorsDetailFilters,
  ImportErrorsDetailGroups,
  ImportErrorsDetailEmpty,
} from './styles';

const ALL_TYPES = 'all';

const ImportErrorsDetail = ({
  errors,
  omittedErrors,
  isMercadoLibre,
}: ImportErrorsDetailProps): ReactElement => {
  const [selectedType, setSelectedType] = useState<string>(ALL_TYPES);
  const [onlyErrors, setOnlyErrors] = useState(false);

  const typeOptions = useMemo(
    () => getImportErrorTypeOptions(errors),
    [errors],
  );

  const filteredErrors = useMemo(
    () =>
      errors.filter((error) => {
        const matchesType =
          selectedType === ALL_TYPES ||
          String(error.errorTypeId) === selectedType;
        const matchesSeverity = !onlyErrors || !error.imported;

        return matchesType && matchesSeverity;
      }),
    [errors, selectedType, onlyErrors],
  );

  const groups = useMemo(
    () => groupImportErrorsByType(filteredErrors),
    [filteredErrors],
  );

  const handleExport = useCallback(
    () => downloadImportErrorsCSV(errors, isMercadoLibre),
    [errors, isMercadoLibre],
  );

  return (
    <ImportErrorsDetailContainer>
      {omittedErrors > 0 && (
        <Alert severity="warning" variant="outlined">
          {`Se muestran los primeros 500 problemas. Hay ${omittedErrors} más sin detallar.`}
        </Alert>
      )}

      <ImportErrorsDetailControls>
        <ImportErrorsDetailFilters>
          <TextField
            select
            size="small"
            label="Tipo"
            value={selectedType}
            onChange={(event) => setSelectedType(event.target.value)}
            fullWidth
            sx={{ maxWidth: 250 }}
          >
            <MenuItem value={ALL_TYPES}>Todos</MenuItem>

            {typeOptions.map((option) => (
              <MenuItem key={option.value} value={String(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={onlyErrors}
                onChange={(event) => setOnlyErrors(event.target.checked)}
              />
            }
            label="Solo errores"
          />
        </ImportErrorsDetailFilters>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Exportar CSV
        </Button>
      </ImportErrorsDetailControls>

      {groups.length === 0 ? (
        <ImportErrorsDetailEmpty>
          <Typography variant="body2">
            No hay problemas para el filtro seleccionado.
          </Typography>
        </ImportErrorsDetailEmpty>
      ) : (
        <ImportErrorsDetailGroups>
          {groups.map((group) => (
            <ImportErrorsDetailGroup
              key={`import-error-group-${group.errorTypeId}`}
              group={group}
              hideClientCode={isMercadoLibre}
            />
          ))}
        </ImportErrorsDetailGroups>
      )}
    </ImportErrorsDetailContainer>
  );
};

export default memo(ImportErrorsDetail);
